import { useMemo } from 'react'
import cx from 'classnames'

import Styles from './UpgradesAndAddOns.module.scss'

import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useOpenSettings } from 'hooks/useOpenSettings'
import { SettingScreen } from 'components/Settings/types'

import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import Plus from 'blaze-ui/icons/20/Plus'
import { formatPrice } from 'components/Settings/TieredBilling/utils'
import { Check2 } from 'blaze-ui/icons'

const WHITE_LABEL_IMAGE_URL =
  'https://res.cloudinary.com/almanac/image/upload/v1772728554/virtual_marketer_onboarding/white-label.png'
const EXTRA_ACCOUNTS_IMAGE_URL =
  'https://res.cloudinary.com/almanac/image/upload/v1772728673/virtual_marketer_onboarding/extra-accounts.png'

type AddOnType = 'white_label' | 'extra_accounts'

const ADD_ON_CONFIG: Record<AddOnType, { key: PaymentPlanKey; title: string; description: string; imageUrl: string }> =
  {
    white_label: {
      key: PaymentPlanKey.BlazeAutopilotWhiteLabel,
      title: 'White Label',
      description: 'Change the visual identity of your workspace with your logo and colors.',
      imageUrl: WHITE_LABEL_IMAGE_URL,
    },
    extra_accounts: {
      key: PaymentPlanKey.BlazeAutopilotExtraAccounts,
      title: 'Extra Accounts',
      description: 'Additional posting accounts to increase your online presence.',
      imageUrl: EXTRA_ACCOUNTS_IMAGE_URL,
    },
  }

export const AddOnCard = ({
  type,
  onAddToPlan,
  isActive,
}: {
  type: AddOnType
  onAddToPlan: () => void
  isActive: boolean
}) => {
  const { autopilotAddOns } = useRootStore().tierUsageStore
  const { data: currentPlan } = useCurrentPlan()
  const { openSettings } = useOpenSettings()

  const config = ADD_ON_CONFIG[type]

  const monthlyPrice = useMemo(() => {
    const intervalCount = currentPlan?.prices?.[0]?.recurring?.interval_count || 1
    const addon = autopilotAddOns.find((a) => a.key === config.key)
    const price = addon?.prices.find((p) => p.recurring?.interval_count === intervalCount)
    if (!price) return null
    return Math.round(price.unit_amount / 100 / intervalCount)
  }, [autopilotAddOns, currentPlan, config.key])

  if (monthlyPrice === null) return null

  const isWhiteLabel = type === 'white_label'
  const isWhiteLabelActive = isWhiteLabel && isActive

  return (
    <div className={Styles.addOnCard}>
      <div
        className={cx(Styles.imageWrapper, {
          [Styles.whiteLabel]: isWhiteLabel,
          [Styles.extraAccounts]: type === 'extra_accounts',
        })}
      >
        <img src={config.imageUrl} alt="" className={Styles.image} />
      </div>
      <div className={Styles.content}>
        <div className={Styles.titleRow}>
          <Plus size={20} color="var(--dark-90)" />
          <Text variant="primary">{config.title}</Text>
        </div>
        <Text variant="secondary" color="var(--dark-60)" className={Styles.description}>
          {config.description}
        </Text>
      </div>
      <div className={Styles.footer}>
        <span className={Styles.priceRow}>
          <Text variant="primary">${formatPrice(monthlyPrice)}</Text>
          <Text variant="placeholder">/mo</Text>
        </span>
        <Button
          variant="secondary"
          onPress={isWhiteLabelActive ? () => openSettings(SettingScreen.General) : onAddToPlan}
          frontIcon={isWhiteLabelActive ? Check2 : undefined}
        >
          {isWhiteLabelActive ? 'Manage' : 'Add to Plan'}
        </Button>
      </div>
    </div>
  )
}

export const UpgradesAndAddOns = ({
  onAddWhiteLabel,
  onAddExtraAccounts,
}: {
  onAddWhiteLabel: () => void
  onAddExtraAccounts: () => void
}) => {
  const workspace = useCurrentWorkspace()
  const { currentSubscription } = useRootStore().tierUsageStore
  const isWhiteLabelActive = !!workspace?.is_whitelabel

  const hasExtraAccounts = useMemo(() => {
    if (!currentSubscription?.subscription_items) return false
    return currentSubscription.subscription_items.some(
      (item) => item.plan_key === PaymentPlanKey.BlazeAutopilotExtraAccounts,
    )
  }, [currentSubscription])

  return (
    <div className={Styles.upgradesSection}>
      <AddOnCard type="white_label" onAddToPlan={onAddWhiteLabel} isActive={isWhiteLabelActive} />
      <AddOnCard type="extra_accounts" onAddToPlan={onAddExtraAccounts} isActive={hasExtraAccounts} />
    </div>
  )
}
